/** @fileoverview Guides users through a batch of assistant questions in the composer. */

import { useLayoutEffect, useRef, useState } from 'react';
import { Check, CircleHelp, Send } from 'lucide-react';
import type { FormEvent, KeyboardEvent } from 'react';
import type { AskQuestionAnswer, AskQuestionData } from '../../types/chat';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import type { AnswerDraft, AskQuestionCardProps } from './types';

const emptyDraft: AnswerDraft = { selected: [], text: '', ownAnswer: false };

export default function AskQuestionCard({
  questions,
  disabled = false,
  onAnswer,
}: AskQuestionCardProps) {
  const [index, setIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>({});
  const [sending, setSending] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const wasDisabled = useRef(disabled);
  useLayoutEffect(() => {
    if (wasDisabled.current && !disabled) setIndex(0);
    wasDisabled.current = disabled;
  }, [disabled]);
  const currentIndex = Math.min(index, questions.length - 1);
  const question = questions[currentIndex];
  const questionId = question?.id;
  const draft = question ? drafts[question.id] ?? emptyDraft : emptyDraft;
  const ownAnswerTextareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (questionId && draft.ownAnswer && !disabled) ownAnswerTextareaRef.current?.focus();
  }, [disabled, draft.ownAnswer, questionId]);

  if (!question) return null;
  const options = question.options ?? [];
  // The free-text answer is one additional choice when max isn't explicit.
  const maxSelectionsFor = (item: AskQuestionData) =>
    item.maxSelections ?? (item.options?.length ?? 0) + 1;
  const canAddOwnAnswer =
    question.inputType !== 'multi_select' || draft.selected.length < maxSelectionsFor(question);
  const answerFor = (item: AskQuestionData) => {
    const answerDraft = drafts[item.id] ?? emptyDraft;
    const itemOptions = item.options ?? [];
    const labels = answerDraft.selected
      .map((value) => itemOptions.find((option) => option.value === value)?.label)
      .filter((label): label is string => Boolean(label));
    const typed = answerDraft.ownAnswer ? answerDraft.text.trim() : '';
    const answer =
      item.inputType === 'multi_select'
        ? [...labels, ...(typed ? [typed] : [])].join(', ')
        : typed || labels.join(', ');
    const selectionCount = answerDraft.selected.length + (typed ? 1 : 0);
    const valid =
      item.inputType === 'multi_select'
        ? selectionCount >= (item.minSelections ?? 1) && selectionCount <= maxSelectionsFor(item)
        : Boolean(answer);
    return valid && answer ? { question_id: item.id, answer } : null;
  };
  const allAnswers = questions.map(answerFor);
  const readyToAdvance = Boolean(answerFor(question));
  const readyToSend = allAnswers.every((answer) => answer !== null);
  const updateDraft = (update: (current: AnswerDraft) => AnswerDraft) => {
    setDrafts((current) => ({
      ...current,
      [question.id]: update(current[question.id] ?? emptyDraft),
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (disabled || sending || !readyToSend) return;
    setSending(true);
    setSubmissionError(undefined);
    try {
      await onAnswer(allAnswers.filter((answer): answer is AskQuestionAnswer => answer !== null));
    } catch {
      setSubmissionError('Your answers could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const onEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (!readyToAdvance) return;
    if (currentIndex < questions.length - 1) setIndex(currentIndex + 1);
    else event.currentTarget.form?.requestSubmit();
  };

  const toggleOption = (value: string) => {
    if (disabled || sending) return;
    updateDraft((current) => {
      if (question.inputType === 'single_select') {
        return { ...current, selected: [value], ownAnswer: false, text: '' };
      }
      if (current.selected.includes(value)) {
        return { ...current, selected: current.selected.filter((item) => item !== value) };
      }
      const hasOwnAnswer = current.ownAnswer && Boolean(current.text.trim());
      if (current.selected.length + Number(hasOwnAnswer) >= maxSelectionsFor(question)) {
        return current;
      }
      return {
        ...current,
        selected: [...current.selected, value],
        ownAnswer: hasOwnAnswer,
        text: hasOwnAnswer ? current.text : '',
      };
    });
  };

  const selectOwnAnswer = () => {
    if (disabled || sending || !canAddOwnAnswer) return;
    updateDraft((current) => ({
      ...current,
      selected: question.inputType === 'single_select' ? [] : current.selected,
      ownAnswer: true,
    }));
  };

  const updateOwnAnswer = (value: string, textarea: HTMLTextAreaElement) => {
    updateDraft((current) => ({ ...current, text: value }));
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };
  const submitWorkflowAnswer = async (answer: string) => {
    if (disabled || sending) return;
    setSending(true);
    setSubmissionError(undefined);
    try {
      await onAnswer([{ question_id: question.id, answer }]);
    } catch {
      setSubmissionError('Your response could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (question.workflowApproval) {
    return (
      <section
        className="mt-1 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface-card p-3"
        aria-label="Plan approval"
        aria-busy={sending}
      >
        <div className="mb-3 flex items-start gap-2.5">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-app-brand-subtle text-app-brand-subtle-foreground">
            <CircleHelp className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-app-text-muted">Plan ready for review</p>
            <p className="mt-1 text-sm leading-5 text-app-text">
              Review the latest plan in the Plan preview. Approving will continue to the next phase.
            </p>
          </div>
        </div>
        <label
          htmlFor={`requested-plan-changes-${question.id}`}
          className="grid gap-1 text-xs font-medium text-app-text-secondary"
        >
          Request changes or add guidance
          <Textarea
            id={`requested-plan-changes-${question.id}`}
            className="min-h-16 resize-y bg-app-surface-muted/50 text-sm"
            disabled={disabled || sending}
            maxLength={2000}
            placeholder="Describe what you’d like changed…"
            rows={2}
            value={draft.text}
            onChange={(event) => updateOwnAnswer(event.target.value, event.currentTarget)}
          />
        </label>
        {submissionError && (
          <p className="mt-2 text-xs text-app-danger" role="alert">
            {submissionError}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || sending}
            onClick={() => void submitWorkflowAnswer('approval:cancel')}
          >
            Cancel for now
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || sending || !draft.text.trim()}
            onClick={() => void submitWorkflowAnswer(`approval:changes: ${draft.text.trim()}`)}
          >
            Request changes
          </Button>
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={disabled || sending}
            onClick={() => void submitWorkflowAnswer('approval:approve')}
          >
            {question.workflowApproval.buttonLabel}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="mt-1 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface-card p-2.5 transition-colors hover:border-app-brand-strong"
      aria-label="Assistant questions"
    >
      <div className="mb-3 flex items-start gap-2.5">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-app-brand-subtle text-app-brand-subtle-foreground">
          <CircleHelp className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          {questions.length > 1 && (
            <p className="mb-1 text-xs font-semibold text-app-brand">
              Question {currentIndex + 1} / {questions.length}
            </p>
          )}
          {question.title && (
            <p className="mb-1 text-xs font-semibold text-app-brand">{question.title}</p>
          )}
          <p className="text-sm font-medium leading-5 text-app-text">{question.question}</p>
          {question.note && (
            <p className="mt-1 text-xs leading-4 text-app-text-muted">{question.note}</p>
          )}
        </div>
      </div>

      {submissionError && (
        <p className="mb-2 text-xs text-app-danger" role="alert">
          {submissionError}
        </p>
      )}
      <form className="flex flex-col gap-2" onSubmit={submit}>
        <p className="text-xs text-app-text-muted">
          {question.inputType === 'multi_select'
            ? 'Select all that apply.'
            : 'Choose one, or choose “Type your own answer”.'}
        </p>
        <div className="flex flex-col gap-2" role="group" aria-label="Answer options">
          {options.map((option) => {
            const isSelected = draft.selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled || sending}
                aria-pressed={isSelected}
                className={`flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default disabled:opacity-60 ${
                  isSelected
                    ? 'border-app-brand bg-app-brand-subtle text-app-brand'
                    : 'border-app-border bg-app-surface-card text-app-text hover:border-app-brand/50 hover:bg-app-surface-muted'
                }`}
                onClick={() => toggleOption(option.value)}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center border border-current ${
                    question.inputType === 'multi_select' ? 'rounded' : 'rounded-full'
                  }`}
                >
                  {isSelected &&
                    (question.inputType === 'multi_select' ? (
                      <Check className="h-3 w-3" aria-hidden="true" />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-current" />
                    ))}
                </span>
                {option.label}
              </button>
            );
          })}
          {draft.ownAnswer ? (
            <div className="flex min-h-10 items-start gap-2 rounded-lg border border-app-brand bg-app-brand-subtle px-3 py-2 text-sm text-app-brand">
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border border-current ${
                  question.inputType === 'multi_select' ? 'rounded' : 'rounded-full'
                }`}
              >
                {question.inputType === 'multi_select' ? (
                  <Check className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-current" />
                )}
              </span>
              <Textarea
                ref={ownAnswerTextareaRef}
                aria-label="Your answer"
                className="min-h-6 flex-1 resize-none rounded-none border-0 bg-transparent p-0 text-sm text-app-text shadow-none focus-visible:ring-0"
                disabled={disabled || sending}
                maxLength={2000}
                placeholder="Type your answer"
                rows={1}
                value={draft.text}
                onChange={(event) => updateOwnAnswer(event.target.value, event.currentTarget)}
                onKeyDown={onEnter}
              />
            </div>
          ) : (
            <button
              type="button"
              disabled={disabled || sending || !canAddOwnAnswer}
              aria-pressed={false}
              className="flex min-h-10 items-center gap-2 rounded-lg border border-app-border bg-app-surface-card px-3 py-2 text-left text-sm text-app-text transition-colors hover:border-app-brand/50 hover:bg-app-surface-muted disabled:cursor-default disabled:opacity-60"
              onClick={selectOwnAnswer}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center border border-current ${
                  question.inputType === 'multi_select' ? 'rounded' : 'rounded-full'
                }`}
              />
              Type your own answer
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={currentIndex === 0 || sending}
            onClick={() => setIndex(Math.max(0, currentIndex - 1))}
          >
            Back
          </Button>
          {currentIndex < questions.length - 1 ? (
            <Button
              type="button"
              size="sm"
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
              disabled={!readyToAdvance || disabled || sending}
              onClick={() => setIndex(currentIndex + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              size="sm"
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
              disabled={!readyToSend || disabled || sending}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {questions.length > 1 ? 'Send answers' : 'Send answer'}
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}
